import { useCallback, useRef } from 'react';
import type { MouseEvent as MouseEventReact, PointerEvent as PointerEventReact } from 'react';

/**
 * Sapuan kiri/kanan untuk berpindah halaman (§G8.2, 31 Agustus 2026).
 *
 * Diminta lapangan untuk ponsel dan layar sentuh: *"utk mode grid fullscreen
 * dan slideshow, itu kan ada bar button utk next & previous page ya, bikin dong
 * di hp bisa swap kiri kanan jg biar bisa cepet."* Yang menggantikan bukan
 * tombolnya — pil `‹`/`›` tetap ada dan tetap satu-satunya kendali yang bisa
 * dipakai remote TV — melainkan langkah menuju tombolnya: di layar penuh
 * kendalinya tidur sesudah 4 detik, jadi berpindah halaman dengan jari berarti
 * ketuk dulu untuk membangunkan, cari pilnya, baru tekan. Sapuan memotong
 * ketiganya jadi satu gerakan.
 *
 * **Hanya sentuhan.** Penunjuk tetikus sengaja tidak ikut: menyeret di atas
 * gambar sudah punya arti sendiri (menggeser gambar yang di-zoom), dan seretan
 * tetikus yang diam-diam memindahkan halaman adalah halaman yang berpindah
 * tanpa ada yang memintanya.
 */

/**
 * Jarak mendatar minimal yang dianggap sapuan, piksel CSS.
 *
 * 60px ≈ 15% lebar ponsel 390px — cukup jauh untuk tidak pernah tertukar dengan
 * ketukan yang jarinya sedikit bergeser (ketukan biasa bergeser < 10px), cukup
 * pendek untuk diselesaikan satu ibu jari tanpa memindahkan genggaman.
 */
const GESER_MIN = 60;

/**
 * Sapuan harus **jelas** mendatar: `|dx|` minimal sekian kali `|dy|`.
 *
 * Ibu jari bergerak melengkung, jadi menuntut `dy` nol tidak realistis; yang
 * dituntut cuma arahnya tidak ambigu. Tanpa syarat ini, seretan miring apa pun
 * berakhir sebagai pergantian halaman.
 */
const RASIO_MENDATAR = 1.5;

/** Lebih lama dari ini bukan sapuan lagi, melainkan jari yang tergeletak. */
const GESER_MAKS_MS = 800;

/**
 * Klik yang datang secepat ini sesudah sapuan adalah **ekor** sapuan itu, dan
 * ditelan.
 *
 * Sapuan yang dimulai di atas sebuah tile berakhir dengan `click` pada tile itu
 * — dan di layar penuh, klik tile berarti membuka kameranya (`§G8.2`). Tanpa
 * penelan ini, setiap sapuan memindahkan halaman **lalu** melompat ke satu
 * kamera. Berjaga waktu, bukan bendera yang menetap: sapuan yang berakhir di
 * ruang kosong tidak diikuti klik apa pun, dan bendera yang tidak pernah
 * dihapus akan menelan klik sah berikutnya.
 */
const EKOR_KLIK_MS = 500;

/**
 * @param onGeser Dipanggil dengan `1` untuk sapuan ke kiri (halaman berikutnya,
 *   arah yang sama dengan menggeser kertas) dan `-1` untuk sapuan ke kanan.
 * @param aktif Sapuan dimatikan seluruhnya kalau `false` — dipakai saat ada
 *   lapisan lain di atas yang punya gesturnya sendiri.
 */
export function useGeserJari(onGeser: (arah: 1 | -1) => void, aktif = true) {
  /** Titik awal sapuan yang sedang dinilai. `null` = tidak ada calon. */
  const awalRef = useRef<{ x: number; y: number; t: number; id: number } | null>(null);
  /** Jari yang sedang menyentuh. Dua di antaranya berarti cubitan, bukan sapuan. */
  const jariRef = useRef(new Set<number>());
  /** Kapan sapuan terakhir selesai — lihat `EKOR_KLIK_MS`. */
  const sapuTerakhirRef = useRef(0);

  const onPointerDown = useCallback(
    (e: PointerEventReact) => {
      if (!aktif || e.pointerType === 'mouse') return;
      jariRef.current.add(e.pointerId);
      // Jari kedua membatalkan calon yang sedang berjalan: dua jari di atas
      // gambar adalah cubitan zoom, dan cubitan yang berakhir sedikit miring
      // tidak boleh ikut memindahkan halaman.
      if (jariRef.current.size > 1) {
        awalRef.current = null;
        return;
      }
      awalRef.current = { x: e.clientX, y: e.clientY, t: Date.now(), id: e.pointerId };
    },
    [aktif],
  );

  const onPointerUp = useCallback(
    (e: PointerEventReact) => {
      jariRef.current.delete(e.pointerId);
      const awal = awalRef.current;
      awalRef.current = null;
      if (!aktif || !awal || awal.id !== e.pointerId) return;

      const dx = e.clientX - awal.x;
      const dy = e.clientY - awal.y;
      if (Date.now() - awal.t > GESER_MAKS_MS) return;
      if (Math.abs(dx) < GESER_MIN || Math.abs(dx) < Math.abs(dy) * RASIO_MENDATAR) return;

      sapuTerakhirRef.current = Date.now();
      onGeser(dx < 0 ? 1 : -1);
    },
    [aktif, onGeser],
  );

  const onPointerCancel = useCallback((e: PointerEventReact) => {
    jariRef.current.delete(e.pointerId);
    awalRef.current = null;
  }, []);

  /**
   * Fase **tangkap**, bukan bubbling: klik ekor sapuan harus dihentikan sebelum
   * sampai ke tombol tile yang jadi sasarannya, dan tombol itu ada di bawah
   * elemen ini.
   */
  const onClickCapture = useCallback((e: MouseEventReact) => {
    if (Date.now() - sapuTerakhirRef.current > EKOR_KLIK_MS) return;
    sapuTerakhirRef.current = 0;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return { onPointerDown, onPointerUp, onPointerCancel, onClickCapture };
}
